import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSeo } from "../lib/seo";
import Reveal from "../components/Reveal";

export default function Consultation() {
  const [page, setPage] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", topic: "", message: "", time_pref: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  useSeo({ title: "Konsultasi — Heri Sensei", description: "Ajukan konsultasi dengan Heri Sensei: pendidikan, karakter, Jepang, bisnis, dan kepemimpinan.", path: "/konsultasi" });

  useEffect(() => {
    api.get("/content/consultation").then((r) => setPage(r.data)).catch(() => setPage({}));
  }, []);

  const c = page || {};
  const services = (c.services || []).filter((s) => s.active !== false && s.title);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/consultation", form);
      setDone(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="consultation-page" className="max-w-6xl mx-auto px-4 sm:px-8 py-14 md:py-20">
      <div className="max-w-2xl">
        <p className="text-xs uppercase tracking-[0.22em] font-semibold text-enji">{c.eyebrow || "Konsultasi"}</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-4 leading-tight text-ink">{c.title || "Diskusi personal yang tenang dan fokus."}</h1>
        <p className="text-sub text-lg leading-relaxed mt-5">{c.intro}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-14">
        <div className="lg:col-span-6 space-y-12">
          {services.length > 0 && (
            <Reveal>
              <h2 className="font-serif text-2xl text-ink">{c.services_heading || "Layanan"}</h2>
              <div className="mt-6 space-y-4">
                {services.map((s, i) => (
                  <div key={i} data-testid={`consultation-service-${i}`} className="bg-surface border border-line rounded-lg p-6">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <h3 className="font-serif text-xl text-ink">{s.title}</h3>
                      {(s.duration || s.format || s.price) && (
                        <span className="text-[12px] uppercase tracking-[0.12em] font-semibold text-faint">
                          {[s.duration, s.format, s.price].filter(Boolean).join(" · ")}
                        </span>
                      )}
                    </div>
                    <p className="text-sub text-[15px] leading-relaxed mt-2">{s.description}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          )}

          {c.who_for && (
            <Reveal>
              <h2 className="font-serif text-2xl text-ink">{c.who_for_heading || "Siapa yang Bisa Terbantu"}</h2>
              <p className="text-sub leading-relaxed mt-4">{c.who_for}</p>
            </Reveal>
          )}

          {(c.topics || []).length > 0 && (
            <Reveal>
              <h2 className="font-serif text-2xl text-ink">{c.topics_heading || "Topik"}</h2>
              <div className="flex flex-wrap gap-2.5 mt-5" data-testid="consultation-topics">
                {c.topics.map((t, i) => (
                  <span key={i} className="px-4 py-2 rounded-full border border-line bg-surface text-sm text-ink">{t}</span>
                ))}
              </div>
            </Reveal>
          )}

          {(c.process || []).length > 0 && (
            <Reveal>
              <h2 className="font-serif text-2xl text-ink">{c.process_heading || "Alur Pengajuan"}</h2>
              <ol className="mt-6 space-y-5">
                {c.process.map((p, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="w-9 h-9 rounded-full bg-enji/10 text-enji flex items-center justify-center font-serif text-lg shrink-0">{i + 1}</span>
                    <div>
                      <p className="font-semibold text-ink">{p.title}</p>
                      <p className="text-sub text-[15px] leading-relaxed mt-1">{p.description}</p>
                    </div>
                  </li>
                ))}
              </ol>
              {c.scheduling_note && <p className="text-[13px] text-faint mt-6">{c.scheduling_note}</p>}
            </Reveal>
          )}

          {(c.faq || []).length > 0 && (
            <Reveal>
              <h2 className="font-serif text-2xl text-ink">{c.faq_heading || "Pertanyaan Umum"}</h2>
              <div className="mt-6 space-y-3">
                {c.faq.map((f, i) => (
                  <details key={i} className="group bg-surface border border-line rounded-lg">
                    <summary className="flex items-center justify-between cursor-pointer list-none p-5 font-semibold text-[15px] text-ink">
                      {f.q}
                      <span className="text-enji group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                    </summary>
                    <p className="px-5 pb-5 text-sub text-[15px] leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </Reveal>
          )}
        </div>

        <div className="lg:col-span-6">
          <Reveal delay={0.1}>
            <div className="bg-surface border border-line rounded-xl p-6 sm:p-8 lg:sticky lg:top-24" data-testid="consultation-form-card">
              <h2 className="font-serif text-2xl text-ink">{c.form_title || "Formulir Pengajuan"}</h2>
              <p className="text-sub text-sm mt-2">{c.form_note || "Pengajuan akan dikonfirmasi melalui email/WhatsApp — bukan pemesanan otomatis."}</p>

              {done ? (
                <div data-testid="consultation-success" className="mt-6 flex gap-3 bg-moss/10 border border-moss/30 rounded-lg p-5">
                  <CheckCircle2 className="text-moss shrink-0 mt-0.5" size={20} />
                  <p className="text-ink text-[15px] leading-relaxed">{done.message} Kami akan menghubungi Anda setelah pengajuan diperiksa.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="cs-name" className="field-label">Nama lengkap *</label>
                    <input id="cs-name" data-testid="consultation-name-input" required value={form.name} onChange={set("name")} className="field-input" placeholder="Nama Anda" />
                  </div>
                  <div>
                    <label htmlFor="cs-email" className="field-label">Email *</label>
                    <input id="cs-email" data-testid="consultation-email-input" type="email" required value={form.email} onChange={set("email")} className="field-input" placeholder="nama@email.com" />
                  </div>
                  <div>
                    <label htmlFor="cs-wa" className="field-label">Nomor WhatsApp (opsional)</label>
                    <input id="cs-wa" data-testid="consultation-whatsapp-input" value={form.whatsapp} onChange={set("whatsapp")} className="field-input" placeholder="+62…" />
                  </div>
                  <div>
                    <label htmlFor="cs-topic" className="field-label">Topik</label>
                    <input id="cs-topic" data-testid="consultation-topic-input" list="consultation-topics-list" value={form.topic} onChange={set("topic")} className="field-input" placeholder="Mis. belajar ke Jepang" />
                    <datalist id="consultation-topics-list">
                      {(c.topics || []).map((t, i) => <option key={i} value={t} />)}
                    </datalist>
                  </div>
                  <div>
                    <label htmlFor="cs-msg" className="field-label">Ringkasan kebutuhan *</label>
                    <textarea id="cs-msg" data-testid="consultation-message-input" required value={form.message} onChange={set("message")} className="field-input" placeholder="Ceritakan singkat apa yang ingin Anda diskusikan…" />
                  </div>
                  <div>
                    <label htmlFor="cs-time" className="field-label">Preferensi waktu (opsional)</label>
                    <input id="cs-time" data-testid="consultation-time-input" value={form.time_pref} onChange={set("time_pref")} className="field-input" placeholder="Mis. sore hari WITA" />
                  </div>
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hp-field" aria-hidden="true" value={form.website} onChange={set("website")} />
                  {error && <p data-testid="consultation-error" role="alert" className="text-sm text-enji">{error}</p>}
                  <button type="submit" data-testid="consultation-submit-button" disabled={loading} className="btn-primary w-full">
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    {c.submit_label || "Kirim Pengajuan"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
